import { z } from 'zod';
// Per-staff salary configuration + the box-level defaults that back it.
//
// TWO LAYERS:
//   • SalaryComponents — the actual values for the four components (base,
//     weekend, travel, sales). The box-level DEFAULTS are exactly this shape,
//     and the engine consumes this shape (the RESOLVED components for a staff
//     member). It's also what computePayroll reads.
//   • StaffSalarySettings — the per-staff record. Each component is a 3-state
//     OVERRIDE: 'default' (inherit the box default), 'none' (explicitly off for
//     this staff), or 'custom' (this staff's own value). Resolution: custom →
//     its value; none → the component OFF; default → the box default.
//
// NOTE: the one-time bonus is deliberately NOT here — it's a free-form per-period
// amount entered on the report.
const Money = z.number().nonnegative();
const Percent = z.number().min(0).max(100);
/** Like Money, but allowed to go negative — for components that can DEDUCT
 *  rather than only add (see daily_class_adjustment). */
const SignedMoney = z.number().min(-100_000).max(100_000);
// ── Base salary ───────────────────────────────────────────────────────────
// Fixed monthly amount, or (time_clock mechanism only) an hourly rate applied
// to the staff member's clocked hours for the period. Coaches paid per class
// (group_classes/hugim) may only use 'none'/'fixed' — 'hourly' is rejected at
// the write layer unless the staff member has the time_clock mechanism.
export const BaseSalarySchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('none') }),
    z.object({ type: z.literal('fixed'), monthly_amount: Money }),
    z.object({ type: z.literal('hourly'), hourly_rate: Money }),
]);
// ── Weekend bonus ───────────────────────────────────────────────────────────
// A supplement on weekend meetings, with INDEPENDENT Friday / Saturday values.
// `mode` picks how the supplement is computed (one toggle for the whole weekend):
//   • 'percent' — % uplift on the meeting's Arbox class pay (e.g. Fri 5%, Sat 10%)
//   • 'fixed'   — a flat ₪ amount added PER weekend session (e.g. ₪10 per Sat class)
// A 0 value (percent or amount) for a day contributes nothing.
export const WeekendBonusSchema = z.object({
    enabled: z.boolean(),
    mode: z.enum(['percent', 'fixed']).default('percent'),
    friday_percent: Percent,
    saturday_percent: Percent,
    /** Fixed ₪ added per Friday session when mode = 'fixed'. */
    friday_amount: Money.default(0),
    /** Fixed ₪ added per Saturday session when mode = 'fixed'. */
    saturday_amount: Money.default(0),
});
// ── Travel ──────────────────────────────────────────────────────────────────
// Fixed monthly, per workday, or per kilometer — paid per DAY worked, not per
// session. (No weekday/weekend distinction.)
//   • per_km: the business sets a single ₪/km `rate_per_km` (its policy); the
//     per-location one-way distances are per-staff and live SEPARATELY in
//     StaffSalarySettings.travel_km_locations (distances are personal). Pay, per
//     configured location = rate × (distance × 2 round trip) × the coach's
//     distinct meeting-days at that location, summed over locations. See
//     computePayroll.
export const TravelSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('none') }),
    z.object({ type: z.literal('fixed_monthly'), monthly_amount: Money }),
    z.object({ type: z.literal('per_workday'), per_day_amount: Money }),
    z.object({ type: z.literal('per_km'), rate_per_km: Money }),
]);
// Per-staff one-way distance (home→business) for a specific Arbox location, used
// by the per_km travel mode. The location NAME matches the meeting's location
// (same catalog the P&L units + class bonuses use).
export const TravelKmLocationSchema = z.object({
    location: z.string().trim().min(1).max(200),
    /** One-way distance in km; the engine doubles it for the round trip. */
    distance_km: Money,
});
// ── Empty-class pay ──────────────────────────────────────────────────────────
// Some boxes pay a coach a (usually reduced) FIXED ₪ for a class that OCCURRED
// but had zero of whatever the box pays for (per the box `class_calc_method` —
// check-ins or registrations, ± late cancels). When enabled, such "empty"
// meetings are pulled OUT of regular class pay and paid this flat amount instead,
// surfaced as its own report category. When disabled, empty classes pay their
// Arbox salary as usual (unchanged behaviour).
export const EmptyClassPaySchema = z.object({
    enabled: z.boolean(),
    /** Flat ₪ paid per empty class. */
    amount: Money,
});
// ── Cancelled-class pay ──────────────────────────────────────────────────────
// A class Arbox marks `cancelled` never happened, so by default it pays nothing
// (see classOccurred). Some boxes still owe the coach part of the fee — a class
// the box called off the night before is not the same as one cancelled for lack
// of registrations. Arbox now returns the cancellation REASON on the report
// (`cancel_reason_id` + `cancel_reason`), so the box sets a percentage of the
// class fee PER REASON. Everything defaults to 0% → unchanged behaviour.
//
// Box-level only: there is no per-coach override (the rules are the box's policy
// on why a class died, not a property of the coach).
export const CancelledClassPaySchema = z.object({
    enabled: z.boolean(),
    /** % of the class fee paid for a cancellation whose reason has no rule of its
     *  own — including a cancellation recorded with NO reason at all. */
    default_percent: Percent,
});
// One reason → % rule. `cancel_reason_id` is Arbox's id for the reason (stable,
// box-scoped); the name is stored alongside only so the settings screen can label
// a reason that hasn't appeared in the current period's data.
export const CancelReasonPayRuleSchema = z.object({
    cancel_reason_id: z.number().int(),
    cancel_reason_name: z.string().trim().max(200).nullable().default(null),
    /** % of the class fee paid when a class is cancelled for this reason. */
    percent: Percent,
});
// ── Volume bonus (בונוס מדרגות) ───────────────────────────────────────────────
// A ₪ supplement per class that grows with HOW MANY classes the coach taught
// that period. The ladder is MARGINAL, like income-tax brackets: every class
// pays the rate of the bracket it falls in, and a coach who never reaches the
// next bracket still earns the lower ones.
//
//   from class 1 → ₪20 · from class 31 → ₪30
//     25 classes → 25×20                     = ₪500
//     30 classes → 30×20                     = ₪600
//     50 classes → 30×20 + 20×30             = ₪1,200
//
// A ladder that doesn't start at 1 is legal and means what it says: with a
// single tier "from class 31 → ₪30", the first 30 classes earn nothing extra
// and only classes 31+ pay.
//
// The count is the line's PAID classes — classes that occurred and paid regular
// class pay. Empty classes (paid the flat empty-class rate) and cancelled
// classes are not on the ladder. In a P&L-split box the count is per BRANCH
// line, like every other per-meeting component.
export const VolumeBonusTierSchema = z.object({
    /** First class number this tier covers (1-based, inclusive). It runs until the
     *  next tier's from_class, or forever when it's the last one. */
    from_class: z.number().int().min(1).max(10_000),
    /** Fixed ₪ added per class inside this tier. */
    amount: Money,
});
export const VolumeBonusSchema = z.object({
    enabled: z.boolean(),
    /** The ladder, in any order — the engine sorts and bounds it (see
     *  volumeBonusTierBreakdown). Capped so a typo can't build a 10k-row ladder. */
    tiers: z.array(VolumeBonusTierSchema).max(20).default([]),
});
// ── Daily class-count adjustment (התאמת שכר לפי כמות שיעורים ביום) ──────────
// A ± ₪-per-class ADJUSTMENT on top of the Arbox class pay, decided by HOW MANY
// classes the coach taught on that SAME day. Unlike the volume bonus, this is
// NOT marginal: the tier with the highest from_count ≤ that day's paid-class
// count is the ONLY one that applies, and it applies to every class that day.
//
//   from 1 class/day → ₪0 (no adjustment) · from 2 → −₪10 · from 3 → −₪30
//     a day with 1 class  → 1×0    = ₪0
//     a day with 2 classes → 2×(−10) = −₪20
//     a day with 3 classes → 3×(−30) = −₪90
//
// A day whose count is below the lowest from_count gets no adjustment (0). The
// count is the line's PAID classes for that day — same basis as volume_bonus
// and class_count (empty/cancelled classes don't count). In a P&L-split box the
// count is per BRANCH LINE, like every other per-meeting component.
export const DailyClassAdjustmentTierSchema = z.object({
    /** First daily class count this tier covers (1-based, inclusive). It applies
     *  until the next tier's from_count, or forever when it's the last one. */
    from_count: z.number().int().min(1).max(1000),
    /** ₪ added (or, if negative, deducted) per class on a day that reaches this
     *  tier. */
    adjustment: SignedMoney,
});
export const DailyClassAdjustmentSchema = z.object({
    enabled: z.boolean(),
    /** The ladder, in any order — the engine sorts and bounds it (see
     *  normalizeDailyClassAdjustmentTiers). Capped so a typo can't build a
     *  10k-row ladder. */
    tiers: z.array(DailyClassAdjustmentTierSchema).max(20).default([]),
});
// ── Per-class bonus (advanced, per-staff only) ──────────────────────────────
// A coach who teaches a specific class (by name) at a specific location earns a
// fixed ₪ supplement PER SESSION of that class, on top of the Arbox class pay.
// Optionally limited to certain days of the week (Saturday Pilates pays more than
// Sunday Pilates). Per-staff only — there is no box-level default for these rules.
export const ClassBonusRuleSchema = z.object({
    class_name: z.string().trim().min(1).max(200),
    location: z.string().trim().min(1).max(200),
    /** Days of the week the rule applies to (0 = Sunday … 6 = Saturday). Absent or
     *  empty ⇒ every day. A day-specific rule beats an every-day rule for the same
     *  class + location. */
    days: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    /** Fixed ₪ added per matching session. */
    amount: Money,
});
// ── Per-קטגוריה rate (advanced, per-staff only) ─────────────────────────────
// A קטגוריה (Arbox box_categories_groups — the report's `box_category_group`)
// bundles several class types. A coach can be paid ONE rate for every class in
// it ("שחייה" = ₪150 per session) instead of a rate per class type in Arbox.
// Unlike the class bonus this is NOT a supplement: a matching meeting pays this
// rate INSTEAD of Arbox's per-meeting salary. No match ⇒ Arbox's salary, as before.
// Matched by Arbox's `box_category_group_id` when the rule carries one (a rename
// in Arbox keeps the rate); rules saved before the id existed match by name.
export const CategoryRateTierSchema = z.object({
    /** Inclusive participant bracket, read against the box's class_calc_method
     *  headcount. max null ⇒ open-ended. */
    min_participants: z.number().int().nonnegative(),
    max_participants: z.number().int().nonnegative().nullable(),
    /** ₪ per session in this bracket. */
    amount: Money,
});
export const CategoryRateRuleSchema = z.object({
    /** Arbox's box_category_group_id. A rule with an id matches by id ONLY;
     *  null on rules saved before classesSummaryReport exposed it (name match). */
    category_id: z.number().int().positive().nullable().default(null),
    /** The קטגוריה's name — the match key only for id-less rules, else display. */
    category: z.string().trim().min(1).max(200),
    /** A branch, or null for every branch. A branch-specific rule beats the
     *  every-branch rule for the same קטגוריה. */
    location: z.string().trim().min(1).max(200).nullable(),
    /** One tier 0–∞ is a flat rate. */
    tiers: z.array(CategoryRateTierSchema).min(1).max(20),
});
export const CategoryRatesSchema = z.array(CategoryRateRuleSchema).max(100);
// ── Per-series rate (a recurring class — "שיעור קבוע") ──────────────────────
// The most specific rate: one recurring slot of one class pays its own rate,
// beating any קטגוריה rate and Arbox's salary (Sunday 18:00 Yoga = ₪180 while
// the coach's Yoga elsewhere is ₪120). Like the קטגוריה rate it REPLACES the pay.
//
// A series is Arbox's `series_id` (classesSummaryReport, 2026-09-28), so a
// class that moves to another time or branch keeps its rate. The slot fields
// (class type + branch + weekday + start time) are the series' latest slot, for
// display — and the match key for rules saved before the id existed.
export const SeriesSlotRateRuleSchema = z.object({
    /** Arbox series id. A rule with an id matches by id ONLY; null on rules
     *  saved before the id existed (those match by slot). */
    series_id: z.number().int().positive().nullable().default(null),
    class_name: z.string().trim().min(1).max(200),
    location: z.string().trim().min(1).max(200),
    /** 0 = Sunday … 6 = Saturday. */
    weekday: z.number().int().min(0).max(6),
    /** HH:MM, the meeting's actual start time. */
    start_time: z.string().regex(/^\d{2}:\d{2}$/),
    /** Arbox's label for the series, display only (never matched on). */
    series_name: z.string().trim().max(200).nullable().default(null),
    tiers: z.array(CategoryRateTierSchema).min(1).max(20),
});
// ── Per-branch rate for every series ("תעריף לסניף") ─────────────────────────
// One rate for EVERY recurring class (meeting with an Arbox series_id) held at
// one branch, so a multi-branch box doesn't pick each series by hand. It lives
// in the same series_rates list (same box list + whole-list per-coach override).
// Order: a rule for the specific series beats it; it beats the קטגוריה rates.
// Matched on the meeting's CURRENT branch, so a series that moves follows the
// new branch's rate, and a new series at the branch gets it automatically.
// One-off classes (no series_id) never match it.
export const SeriesBranchRateRuleSchema = z.object({
    scope: z.literal('branch'),
    location: z.string().trim().min(1).max(200),
    tiers: z.array(CategoryRateTierSchema).min(1).max(20),
});
/** A series_rates entry: one series, or every series at one branch. Rules
 *  saved before the branch kind existed have no `scope` ⇒ a series rule. */
export const SeriesRateRuleSchema = z.union([SeriesBranchRateRuleSchema, SeriesSlotRateRuleSchema]);
export const isSeriesBranchRule = (r) => r.scope === 'branch';
export const SeriesRatesSchema = z.array(SeriesRateRuleSchema).max(500);
// ── Conversion bonus (trial → member) ───────────────────────────────────────
// A coach earns a ₪ bonus for every lead who took a TRIAL CLASS with them and
// converted to a member (status "הומר ללקוח") within `window_days` of the trial.
// The ₪ per conversion comes from a ladder keyed by HOW MANY qualifying
// conversions the coach had this period. NOT marginal: the tier with the highest
// from_count ≤ the coach's count is the ONLY one that applies, and it applies to
// EVERY conversion that period (the product decision — "the tier they reached"):
//
//   from 1 → ₪50 · from 5 → ₪70 · from 10 → ₪100
//     4 conversions → 4×50  = ₪200
//     5 conversions → 5×70  = ₪350
//    12 conversions → 12×100 = ₪1,200
//
// A single tier "from 1" is the old flat ₪-per-conversion (mig 042 rewrote every
// stored flat amount into exactly that). `enabled` + `window_days` are box-wide
// GLOBAL settings (live on the box defaults); only the ladder is overridable per
// coach (see the per-staff `conversion_bonus_tiers` override below). Attribution
// is by the TRIAL date.
export const ConversionBonusTierSchema = z.object({
    /** First conversion count this tier covers (1-based, inclusive). It applies
     *  until the next tier's from_count, or forever when it's the last one. */
    from_count: z.number().int().min(1).max(1000),
    /** ₪ paid per conversion — for ALL of the period's conversions — when the
     *  coach's count reaches this tier. */
    amount: Money,
});
/** Capped so a typo can't build a 10k-row ladder. */
export const ConversionBonusTiersSchema = z.array(ConversionBonusTierSchema).max(20);
export const ConversionBonusSchema = z.object({
    enabled: z.boolean(),
    /** The ladder, in any order — the engine sorts and bounds it (see
     *  normalizeConversionTiers). */
    tiers: ConversionBonusTiersSchema.default([]),
    /** Max days between the trial class and the conversion to still qualify. */
    window_days: z.number().int().min(0).max(365),
});
// ── Resolved components / box defaults ──────────────────────────────────────
// The actual values for all components. This is the box-level DEFAULTS shape AND
// the resolved-per-staff shape the engine consumes. (Sales bonus removed — the
// focus is class-based salary; base / weekend / travel only.)
export const SalaryComponentsSchema = z.object({
    base_salary: BaseSalarySchema,
    weekend_bonus: WeekendBonusSchema,
    travel: TravelSchema,
    conversion_bonus: ConversionBonusSchema,
    empty_class_pay: EmptyClassPaySchema,
    /** Marginal ₪-per-class ladder by the line's paid-class count. Defaulted so
     *  anything constructing components before this existed stays valid. */
    volume_bonus: VolumeBonusSchema.default({ enabled: false, tiers: [] }),
    /** Box-wide policy for cancelled classes (the per-reason rules live beside it,
     *  see CancelReasonPayRule — they're a list, not a component value). Defaulted
     *  so anything constructing components before this existed stays valid. */
    cancelled_class_pay: CancelledClassPaySchema.default({ enabled: false, default_percent: 0 }),
    /** ± ₪-per-class ladder keyed by the SAME DAY's paid-class count. Defaulted
     *  so anything constructing components before this existed stays valid. */
    daily_class_adjustment: DailyClassAdjustmentSchema.default({ enabled: false, tiers: [] }),
    /** Rates per קטגוריה that REPLACE Arbox's per-meeting salary (see
     *  CategoryRateRuleSchema). A box-wide list, overridable per coach as a WHOLE
     *  list. Defaulted so anything constructing components before this existed
     *  stays valid. */
    category_rates: CategoryRatesSchema.default([]),
    /** Rates per series (recurring class slot) — checked BEFORE category_rates.
     *  Same box-wide list + whole-list per-coach override. */
    series_rates: SeriesRatesSchema.default([]),
});
// "Everything off" — the component values when nothing is configured.
export const OFF_COMPONENTS = {
    base_salary: { type: 'none' },
    weekend_bonus: {
        enabled: false,
        mode: 'percent',
        friday_percent: 0,
        saturday_percent: 0,
        friday_amount: 0,
        saturday_amount: 0,
    },
    travel: { type: 'none' },
    conversion_bonus: { enabled: false, tiers: [], window_days: 0 },
    empty_class_pay: { enabled: false, amount: 0 },
    volume_bonus: { enabled: false, tiers: [] },
    cancelled_class_pay: { enabled: false, default_percent: 0 },
    daily_class_adjustment: { enabled: false, tiers: [] },
    category_rates: [],
    series_rates: [],
};
// Box-level defaults default to "nothing configured".
export const DEFAULT_BOX_SALARY_DEFAULTS = OFF_COMPONENTS;
// ── 3-state per-staff override ──────────────────────────────────────────────
// 'default' = inherit the box default · 'none' = explicitly off for this staff ·
// 'custom' = use this staff's own value.
export const COMPONENT_SOURCES = ['default', 'none', 'custom'];
function overrideSchema(value) {
    return z.discriminatedUnion('source', [
        z.object({ source: z.literal('default') }),
        z.object({ source: z.literal('none') }),
        z.object({ source: z.literal('custom'), value }),
    ]);
}
export const BaseSalaryOverrideSchema = overrideSchema(BaseSalarySchema);
export const WeekendBonusOverrideSchema = overrideSchema(WeekendBonusSchema);
export const TravelOverrideSchema = overrideSchema(TravelSchema);
export const EmptyClassPayOverrideSchema = overrideSchema(EmptyClassPaySchema);
export const VolumeBonusOverrideSchema = overrideSchema(VolumeBonusSchema);
export const DailyClassAdjustmentOverrideSchema = overrideSchema(DailyClassAdjustmentSchema);
// Per-coach override of the WHOLE קטגוריה rate list: 'default' = the box's list ·
// 'none' = no category rates (Arbox's pay for every class) · 'custom' = the
// coach's own list, which replaces the box's entirely (never merged rule by rule).
export const CategoryRatesOverrideSchema = overrideSchema(CategoryRatesSchema);
// Same, for the series list.
export const SeriesRatesOverrideSchema = overrideSchema(SeriesRatesSchema);
// Per-coach override of ONLY the conversion-bonus ladder: 'default' = use the
// box ladder · 'none' = no conversion bonus for this coach · 'custom' = own
// ladder. (enabled + window_days are global and never overridden per coach.)
export const ConversionTiersOverrideSchema = overrideSchema(ConversionBonusTiersSchema);
// ── שכר גלובלי (per staff) ────────────────────────────────────────────────────
export const GlobalSalarySchema = z
    .object({
    enabled: z.boolean(),
    amount: z.number().min(0, 'סכום השכר הגלובלי לא יכול להיות שלילי').max(10_000_000),
})
    .refine((g) => !g.enabled || g.amount > 0, {
    message: 'יש להזין סכום שכר גלובלי גדול מ-0',
    path: ['amount'],
});
// ── Full per-staff settings record ──────────────────────────────────────────
export const StaffSalarySettingsSchema = z.object({
    arbox_users_boxes_id: z.number().int(),
    base_salary: BaseSalaryOverrideSchema,
    weekend_bonus: WeekendBonusOverrideSchema,
    travel: TravelOverrideSchema,
    /** Per-coach override of the empty-class fixed pay (default/none/custom). */
    empty_class_pay: EmptyClassPayOverrideSchema.default({ source: 'default' }),
    /** Per-coach override of the volume-bonus ladder (default/none/custom). */
    volume_bonus: VolumeBonusOverrideSchema.default({ source: 'default' }),
    /** Per-coach override of the daily class-count adjustment (default/none/custom). */
    daily_class_adjustment: DailyClassAdjustmentOverrideSchema.default({ source: 'default' }),
    /** Per-coach override of the conversion-bonus ladder (enabled + window stay
     *  global). Defaults to inheriting the box ladder. */
    conversion_bonus_tiers: ConversionTiersOverrideSchema.default({ source: 'default' }),
    /** Advanced, per-staff: per-class (name + location) fixed ₪ supplements. */
    class_bonuses: z.array(ClassBonusRuleSchema).default([]),
    /** Per-coach override of the box's קטגוריה rates (default/none/custom). */
    category_rates: CategoryRatesOverrideSchema.default({ source: 'default' }),
    /** Per-coach override of the box's series rates (default/none/custom). */
    series_rates: SeriesRatesOverrideSchema.default({ source: 'default' }),
    /** Per-staff one-way distances per location, for the per_km travel mode. Only
     *  consumed when the resolved travel type is per_km. */
    travel_km_locations: z.array(TravelKmLocationSchema).default([]),
    /** Manually-assigned P&L unit — for staff whose pay isn't location-derived
     *  (hugim/time_clock have no per-session location to auto-attribute a unit
     *  from, unlike group_classes' pickCoachPrimaryUnit). null = use the box's
     *  default/primary unit. Ignored for group_classes staff, whose unit is
     *  still computed from their class locations. */
    pnl_unit_id: z.number().int().nullable().default(null),
    /** שכר גלובלי — a fixed ₪ amount every pay period, no matter what. When
     *  enabled it REPLACES every mechanism's pay for this staff member (classes /
     *  hugim / time clock are ignored); bonus, deduction, VAT and withholding
     *  still apply. For regular employees with no activity in Arbox. */
    global_salary: GlobalSalarySchema.default({ enabled: false, amount: 0 }),
});
// Defaults = "inherit the box default for everything".
export const DEFAULT_STAFF_SALARY_SETTINGS = {
    base_salary: { source: 'default' },
    weekend_bonus: { source: 'default' },
    travel: { source: 'default' },
    empty_class_pay: { source: 'default' },
    volume_bonus: { source: 'default' },
    daily_class_adjustment: { source: 'default' },
    conversion_bonus_tiers: { source: 'default' },
    class_bonuses: [],
    category_rates: { source: 'default' },
    series_rates: { source: 'default' },
    travel_km_locations: [],
    pnl_unit_id: null,
    global_salary: { enabled: false, amount: 0 },
};
// ── Resolution ──────────────────────────────────────────────────────────────
// Resolve a single override against the box default + the per-component OFF.
function resolveOne(override, def, off) {
    if (override.source === 'custom')
        return override.value;
    if (override.source === 'none')
        return off;
    return def;
}
/** Resolve a staff member's overrides against the box defaults into the concrete
 *  component values the engine consumes. */
export function resolveStaffComponents(staff, defaults) {
    return {
        base_salary: resolveOne(staff.base_salary, defaults.base_salary, OFF_COMPONENTS.base_salary),
        weekend_bonus: resolveOne(staff.weekend_bonus, defaults.weekend_bonus, OFF_COMPONENTS.weekend_bonus),
        travel: resolveOne(staff.travel, defaults.travel, OFF_COMPONENTS.travel),
        empty_class_pay: resolveOne(staff.empty_class_pay, defaults.empty_class_pay, OFF_COMPONENTS.empty_class_pay),
        volume_bonus: resolveOne(
        // Tolerate a settings object built before this component existed.
        staff.volume_bonus ?? { source: 'default' }, defaults.volume_bonus ?? OFF_COMPONENTS.volume_bonus, OFF_COMPONENTS.volume_bonus),
        daily_class_adjustment: resolveOne(
        // Tolerate a settings object built before this component existed.
        staff.daily_class_adjustment ?? { source: 'default' }, defaults.daily_class_adjustment ?? OFF_COMPONENTS.daily_class_adjustment, OFF_COMPONENTS.daily_class_adjustment),
        category_rates: resolveOne(
        // Tolerate a settings object built before this component existed.
        staff.category_rates ?? { source: 'default' }, defaults.category_rates ?? [], []),
        series_rates: resolveOne(staff.series_rates ?? { source: 'default' }, defaults.series_rates ?? [], []),
        // Cancelled-class pay is box policy — no per-coach override exists, so the
        // box value applies to everyone as-is.
        cancelled_class_pay: defaults.cancelled_class_pay ?? OFF_COMPONENTS.cancelled_class_pay,
        // enabled + window are always the box-wide values; only the ladder is
        // overridable per coach ('none' → empty ladder = no conversion bonus).
        conversion_bonus: {
            enabled: defaults.conversion_bonus.enabled,
            window_days: defaults.conversion_bonus.window_days,
            tiers: resolveOne(staff.conversion_bonus_tiers ?? { source: 'default' }, defaults.conversion_bonus.tiers ?? [], []),
        },
    };
}
// ── "Configured" predicates (drive Yes/No tags + all-zero hide) ─────────────
export function isBaseConfigured(b) {
    return b.type !== 'none';
}
export function isWeekendConfigured(w) {
    if (!w.enabled)
        return false;
    return w.mode === 'fixed'
        ? w.friday_amount > 0 || w.saturday_amount > 0
        : w.friday_percent > 0 || w.saturday_percent > 0;
}
export function isTravelConfigured(t) {
    return t.type !== 'none';
}
export function isEmptyClassPayConfigured(e) {
    return e.enabled && e.amount > 0;
}
/** The volume bonus is "configured" once it's on and at least one tier can pay.
 *  An all-zero ladder is the default state and pays exactly nothing, same as off. */
export function isVolumeBonusConfigured(v) {
    return !!v?.enabled && (v.tiers ?? []).some((t) => t.amount > 0);
}
/** The daily class-count adjustment is "configured" once it's on and at least
 *  one tier does something — including a pure deduction, so `!== 0` rather
 *  than `> 0` (unlike the volume bonus, this component can be negative). */
export function isDailyClassAdjustmentConfigured(d) {
    return !!d?.enabled && (d.tiers ?? []).some((t) => t.adjustment !== 0);
}
/** Cancelled-class pay is "configured" once it's on and SOMETHING can pay — the
 *  catch-all percentage or at least one per-reason rule. (All-zero rules are the
 *  default state and pay exactly nothing, same as off.) */
export function isCancelledClassPayConfigured(c, rules) {
    if (!c?.enabled)
        return false;
    return c.default_percent > 0 || (rules ?? []).some((r) => r.percent > 0);
}
/** Whether the RESOLVED components have anything that can produce pay. */
export function hasAnyComponentConfigured(c) {
    return (isBaseConfigured(c.base_salary) ||
        isWeekendConfigured(c.weekend_bonus) ||
        isTravelConfigured(c.travel));
}
//# sourceMappingURL=salary-settings.js.map